import React, { Component, ComponentType, ReactElement, useContext, useEffect, useState } from 'react';

import { createLocation, History, Location } from 'history';
import isAbsoluteUrl from 'is-absolute-url';
import { __RouterContext as RouterContext } from 'react-router';

export interface RouterContextProps {
  href: string;
}

export function useRouterContext(to: string): [string, (to: string) => void] {
  const context = useContext(RouterContext);

  const [target, setTarget] = useState<string>(to);
  const [href, setHref] = useState('');

  useEffect(() => {
    const href = toHref(context.history, target, context.location);
    setHref(href);
  }, [target]);

  return [href, setTarget];
}

// we should be able to swap this for the improved react-router-dom's Link component when
// it lands. For reference: https://github.com/ReactTraining/react-router/pull/6462
function withRouterContext<P extends object>(WrappedComponent: ComponentType<RouterContextProps>, to: string): ComponentType<P> {
  return class WithRouterContext extends Component<P> {
    render(): ReactElement {
      return (
        <RouterContext.Consumer>
          { context => {
            const { history } = context;
            const href = toHref(history, to, context.location);

            return (<WrappedComponent {...this.props} href={href} />);
          }}
        </RouterContext.Consumer>
      );
    }
  };
}

export default withRouterContext;

function toHref(history: History, to: string, currentLocation: Location): string {
  if (isAbsoluteUrl(to)) {
    return to;
  }

  const location = createLocation(to, undefined, undefined, currentLocation);

  return history.createHref(location);
}
